// ============================================================================
// WIDGET SANDBOX — Data Bridge Hook
// Listens for DATA_REQUEST from sandbox iframe, proxies to Django API.
// Routes through React Query so multiple widgets in the same view share one
// network call (in-flight dedup) and cache responses for staleTime.
// ============================================================================

import { useEffect, useCallback, type RefObject } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/services/api/client'
import { ALLOWED_API_PATHS, type DataRequestMessage, type DataResponseMessage } from './types'

export interface BridgeUserInfo {
  id: string
  name: string
}

const DATA_TIMEOUT_MS = 10_000
const WIDGET_CACHE_STALE_MS = 60_000
const allowedPathSet = new Set<string>(ALLOWED_API_PATHS)

// Stable param key for React Query cache lookups.
function stableStringify(value: unknown): string {
  if (value === null || typeof value !== 'object') return JSON.stringify(value)
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(',')}]`
  const entries = Object.entries(value as Record<string, unknown>).sort(([a], [b]) =>
    a.localeCompare(b),
  )
  return `{${entries.map(([k, v]) => `${JSON.stringify(k)}:${stableStringify(v)}`).join(',')}}`
}

export function useWidgetDataBridge(
  iframeRef: RefObject<HTMLIFrameElement | null>,
  enabled: boolean = true,
  userInfo?: BridgeUserInfo,
  targetProfileId?: number | null,
) {
  const queryClient = useQueryClient()

  const sendResponse = useCallback(
    (requestId: string, data?: unknown, error?: string) => {
      const iframe = iframeRef.current
      if (!iframe?.contentWindow) return

      const response: DataResponseMessage = {
        type: 'DATA_RESPONSE',
        requestId,
        ...(data !== undefined && { data }),
        ...(error !== undefined && { error }),
      }
      // Must use '*' because sandboxed iframe has opaque origin
      iframe.contentWindow.postMessage(response, '*')
    },
    [iframeRef],
  )

  const handleFetch = useCallback(
    async (requestId: string, args: unknown[]) => {
      const path = args[0] as string
      const userParams = (args[1] as Record<string, unknown>) ?? {}

      if (!allowedPathSet.has(path)) {
        sendResponse(
          requestId,
          undefined,
          `Disallowed API path: "${path}". Allowed: ${ALLOWED_API_PATHS.join(', ')}`,
        )
        return
      }

      // Always force agent-field filtering on widget-issued requests.
      // Inject target_profile_id when viewing another athlete's data.
      const params: Record<string, unknown> = { ...userParams, fields: 'agent' }
      if (targetProfileId) params.target_profile_id = targetProfileId

      try {
        const data = await queryClient.fetchQuery({
          queryKey: ['widget-data', path, stableStringify(params)],
          staleTime: WIDGET_CACHE_STALE_MS,
          queryFn: async ({ signal }) => {
            const timeoutController = new AbortController()
            const timeout = setTimeout(() => timeoutController.abort(), DATA_TIMEOUT_MS)
            // Forward both signals so React Query cancellation and our timeout both work.
            const onTimeout = () => signal?.dispatchEvent?.(new Event('abort'))
            timeoutController.signal.addEventListener('abort', onTimeout)
            try {
              const response = await apiClient.get(path, {
                params,
                signal: timeoutController.signal,
              })
              return response.data
            } finally {
              clearTimeout(timeout)
            }
          },
        })
        sendResponse(requestId, data)
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Request failed'
        sendResponse(requestId, undefined, message)
      }
    },
    [sendResponse, queryClient, targetProfileId],
  )

  const handleGetUser = useCallback(
    (requestId: string) => {
      sendResponse(requestId, { id: userInfo?.id ?? '', name: userInfo?.name ?? '' })
    },
    [sendResponse, userInfo],
  )

  useEffect(() => {
    if (!enabled) return

    const handleMessage = (event: MessageEvent) => {
      // Validate source is our iframe
      if (event.source !== iframeRef.current?.contentWindow) return

      const msg = event.data as DataRequestMessage
      if (msg?.type !== 'DATA_REQUEST') return

      const { requestId, method, args } = msg

      switch (method) {
        case 'fetch':
          handleFetch(requestId, args)
          break
        case 'getUser':
          handleGetUser(requestId)
          break
        default:
          sendResponse(requestId, undefined, `Unknown method: "${method}"`)
      }
    }

    window.addEventListener('message', handleMessage)
    return () => window.removeEventListener('message', handleMessage)
  }, [enabled, iframeRef, handleFetch, handleGetUser, sendResponse])
}
